import { getLocalStorageData } from '../../../utils/helper';
import { APP_API_URL_V2 } from '../../../utils/xhr-instance';
import { axiosInstanceV2 } from '../../axios';

/**
 * Every request here used to end with:
 *
 *   .catch((error) => error?.response.data)
 *
 * which turned a REJECTION into a RESOLUTION carrying the error body. A caller
 * doing `const { subscription } = await getSubscriptions()` then destructured a
 * failure response, got `undefined`, and stored that as if it were data — so a
 * 401 after a server restart surfaced much later as
 * "Cannot read properties of undefined (reading 'plan_usages')" during render,
 * crashing the page instead of showing an error.
 *
 * It was also unsafe in itself: `error?.response.data` only guards `error`, so a
 * network failure (no `response` at all) threw a second error inside the handler.
 *
 * These now reject. Callers are responsible for handling failure, which is the
 * only way they can render something useful.
 */
function authHeaders() {
	const token = getLocalStorageData();
	return {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${token?.token}`,
	};
}

/** Normalises an axios error into something with a usable message and status. */
function toRequestError(error, fallbackMessage) {
	const status = error?.response?.status ?? null;
	const message =
		error?.response?.data?.message ??
		(status === 401 ? 'Your session has expired. Please sign in again.' : error?.message) ??
		fallbackMessage;

	return Object.assign(new Error(message), {
		status,
		isUnauthorized: status === 401,
		// No response at all means the request never reached the server —
		// usually a restart in progress or the machine being offline.
		isNetworkError: !error?.response,
	});
}

export async function getSubscriptions() {
	try {
		const res = await axiosInstanceV2.get(`${APP_API_URL_V2}/subscription/details`, {
			headers: authHeaders(),
		});
		return res.data?.data ?? {};
	} catch (error) {
		throw toRequestError(error, 'Unable to load subscription details.');
	}
}

export async function cancelScheduledDowngrade() {
	try {
		const res = await axiosInstanceV2.post(
			`${APP_API_URL_V2}/subscription/cancel-scheduled-downgrade`,
			{},
			{ headers: authHeaders() },
		);
		return res.data?.data ?? {};
	} catch (error) {
		throw toRequestError(error, 'Unable to cancel the scheduled downgrade.');
	}
}

export async function getPlanUsages() {
	try {
		const res = await axiosInstanceV2.get(`${APP_API_URL_V2}/settings/organisation/usage`, {
			headers: authHeaders(),
		});
		return res.data?.data ?? {};
	} catch (error) {
		throw toRequestError(error, 'Unable to load plan usage.');
	}
}

export async function getSubscriptionPlans() {
	try {
		const res = await axiosInstanceV2.get(`${APP_API_URL_V2}/plans/`, {
			headers: authHeaders(),
		});
		return res.data?.data ?? [];
	} catch (error) {
		throw toRequestError(error, 'Unable to load plans.');
	}
}
